import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {generateRoutes} from "../../routes";
import {getEnvironmentVariables} from "../../store/actions";
import {useLocation} from "react-router-dom";
import {isLegacySubdomain} from "../../helpers/Util";

const VariableProvider = ({ children }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    let localEnv = localStorage.getItem("env");
    if (localEnv) localEnv = JSON.parse(localEnv);

    // Get environment variables from redux
    const storedEnv = useSelector((state) => state.Variables.env);
    const env = localEnv || storedEnv;

    // Check org from URL and fetch environment variables if necessary
    const checkOrg = useCallback(() => {
        setLoading(true);
        const { authProtectedRoutes, publicRoutes } = generateRoutes();

        const hostname = window.location.hostname;
        const pathname = window.location.pathname;
        const pathSegments = pathname.split("/").filter(Boolean);
        const storedOrg = localStorage.getItem("org");

        const invalidPathSet = new Set(
            [...authProtectedRoutes, ...publicRoutes].map(route =>
                route.path.replace(/^\//, "")
            )
        );

        const isDevDomain = hostname === "localhost" || hostname.startsWith("dev") || hostname.startsWith("staging");
        const isBitgoDomain = hostname.endsWith("bitgo.com");

        let possibleOrg = null;
        const pathOrg = pathSegments.length ? pathSegments[0] : null;

        if (isBitgoDomain || isDevDomain) {
            if (pathOrg && !invalidPathSet.has(pathOrg)) {
                // use path as org by default
                possibleOrg = pathOrg;
            }
        } else {
            // use subdomain org as fallback
            const hostnameParts = hostname.split(".");
            if (hostnameParts.length > 2) {
                const subdomainOrg = hostnameParts[0];
                if (subdomainOrg && !invalidPathSet.has(subdomainOrg)) {
                    possibleOrg = subdomainOrg;
                }
            }
        }

        if (isLegacySubdomain(window.location) && possibleOrg) {
            dispatch(getEnvironmentVariables(possibleOrg));
        } else if (storedOrg) {
            if (possibleOrg && possibleOrg !== storedOrg) {
                localStorage.setItem("org", possibleOrg);
                dispatch(getEnvironmentVariables(possibleOrg));
            } else {
                dispatch(getEnvironmentVariables(storedOrg));
            }
        } else if (possibleOrg) {
            localStorage.setItem("org", possibleOrg);
            dispatch(getEnvironmentVariables(possibleOrg));
        } else {
            dispatch(getEnvironmentVariables());
        }

        setLoading(false);
    }, [dispatch]);

    useEffect(() => {
        // Avoid refetching environment variables on every render
        if (!loading) checkOrg();
    }, [location.pathname, checkOrg, loading]); // Run checkOrg when location.pathname changes

    // Recursively inject props into all children
    const injectPropsToChildren = (children) => {
        return React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
                // Check if the child is a function-based HOC wrapper
                const isHocWrapped = typeof child.type === "function";

                if (isHocWrapped) {
                    return (
                        <child.type {...child.props} env={env}>
                            {child.props.children ? injectPropsToChildren(child.props.children) : null}
                        </child.type>
                    );
                }

                return React.cloneElement(child, { env, ...child.props },
                    child.props.children ? injectPropsToChildren(child.props.children) : null
                );
            }
            return child;
        });
    };

    return <div>{injectPropsToChildren(children)}</div>;
};

export default VariableProvider;
